/**
 * How many chips fit on each of the group bar's two rows.
 *
 * This is the whole of the bar's layout decision, and it lives here rather than
 * in `GroupBar` because jsdom has no layout: a test can measure nothing, so the
 * only testable form of "does it fit" is arithmetic over widths somebody else
 * measured. Same reason `chart-window.ts` and `layout-templates.ts` exist.
 *
 * The component's job is therefore reduced to two things it *can* do in a
 * browser — measure the chips against an off-screen ruler, and read the two
 * containers' widths — and everything downstream of those numbers is here.
 *
 * ## Why the overflow button needs two passes
 *
 * Row two ends in `[+N ▾] [count] [Columns]`, and the chevron is only there
 * when something is hidden. So the budget depends on the answer: reserve room
 * for it and a set that would have fitted exactly loses its last chip to a
 * chevron that then has nothing to show.
 *
 * So the *unreserved* run goes first, and its result stands if it hid nothing —
 * that is the case where no chevron is needed and reserving for one would have
 * been reserving for a button with nothing to show. Only when that run really
 * does leave something over is the reserved run computed and returned, because
 * only then is the chevron's premise true.
 *
 * Doing it the other way round is wrong in a way that is easy to miss: the
 * reserved run hides one chip, which "proves" the chevron is needed, and the
 * bar settles into a state where the chevron exists solely to hold the chip the
 * chevron displaced.
 */

/** Flex gap between chips. Matches the bar's `gap`. */
export const CHIP_GAP = 6;

/** Width reserved for the `+N ▾` button when it is needed. */
export const OVERFLOW_WIDTH = 40;

export interface ChipRows {
  /** Indices on the first row, in order. */
  row1: number[];
  /** Indices on the second row, in order. */
  row2: number[];
  /** Indices behind the overflow chevron, in order. */
  hidden: number[];
}

export interface FillOptions {
  gap?: number;
  overflowWidth?: number;
  /**
   * Indices that must end up visible, whatever it costs the ones around them.
   *
   * The selected group, in practice. Filling strictly in order meant a group
   * chosen from the overflow list stayed *in* the overflow list: the user picked
   * it, the rows did not move, and the only thing that changed was data they had
   * to infer from the grid. A selection nothing on screen reflects reads as a
   * click that did not register.
   *
   * Order is still the list's own — a required chip is not promoted to the
   * front. What gives way is the unselected chips nearest the boundary, which
   * are the ones already closest to being hidden anyway, so the bar's
   * arrangement stays recognisable across a selection.
   */
  required?: readonly number[];
}

/**
 * The cost of the required chips at or after each index, gaps included.
 *
 * `tail[i]` is what still has to be paid for from `i` onward, which is what lets
 * the fill decide whether an *optional* chip is affordable: placing it is only
 * allowed if what remains after it still covers every required chip left. One
 * backwards pass rather than a scan per candidate.
 */
function requiredTail(
  widths: readonly number[],
  required: ReadonlySet<number>,
  gap: number,
): number[] {
  const tail = new Array<number>(widths.length + 1).fill(0);
  for (let i = widths.length - 1; i >= 0; i -= 1) {
    tail[i] = tail[i + 1]! + (required.has(i) ? widths[i]! + gap : 0);
  }
  return tail;
}

/**
 * Fill two rows from `widths`, in order, and report what fits neither.
 *
 * `row1Budget` and `row2Budget` are the *content* widths of the two chip
 * containers — each is already net of whatever else shares its row, because
 * both are the only flexible item there.
 *
 * A width wider than a whole row is placed anyway rather than hidden: a chip
 * nothing can display is still the user's chosen group, and clipping it says so
 * where an empty row plus a `+1` does not.
 */
export function fillRows(
  widths: readonly number[],
  row1Budget: number,
  row2Budget: number,
  options: FillOptions = {},
): ChipRows {
  const gap = options.gap ?? CHIP_GAP;
  const overflowWidth = options.overflowWidth ?? OVERFLOW_WIDTH;
  // A zero-width chevron costs no gap either — otherwise "reserve nothing"
  // still quietly took six pixels.
  const reservation = overflowWidth > 0 ? overflowWidth + gap : 0;
  const required = new Set(options.required ?? []);
  const tail = requiredTail(widths, required, gap);

  const run = (reserve: boolean): ChipRows => {
    const row1: number[] = [];
    const row2: number[] = [];
    const hidden: number[] = [];
    const budget2 = reserve ? row2Budget - reservation : row2Budget;
    let used = 0;
    let used2 = 0;
    /**
     * Which row is still accepting chips — and it only ever moves forward.
     *
     * **An optional chip that does not fit closes its row for good**, rather
     * than being skipped so a narrower chip behind it can slot into the hole.
     * Hole-filling would reorder the bar: the list is the user's own order, and
     * a row reading `SP500 · NASDAQ · RUSSELL` one moment and `SP500 · RUSSELL`
     * the next — because NASDAQ grew a digit — is a bar nobody can scan twice.
     *
     * A **required** chip is the one thing allowed past a closed row, because it
     * is not competing for the space: the accounting below has been holding room
     * for it since the first chip.
     */
    let row: 1 | 2 | 3 = 1;

    for (let index = 0; index < widths.length; index += 1) {
      const width = widths[index]!;
      const must = required.has(index);
      /** What still has to be paid for after this chip. */
      const owed = tail[index + 1]!;

      if (row === 1) {
        // The first chip goes on regardless — see the note above on a chip too
        // wide for any row.
        const forced = row1.length === 0;
        // A required chip spends freely; an optional one must leave behind
        // whatever row two cannot absorb on its own.
        const spare = must ? 0 : Math.max(0, owed - budget2);
        if (forced || used + width + spare <= row1Budget) {
          row1.push(index);
          used += width + gap;
          continue;
        }
        row = 2;
      }

      if (row === 2) {
        // No forced first chip here: row one already guarantees something is on
        // screen, so an empty row two with the rest behind the chevron is
        // honest.
        const spare = must ? 0 : owed;
        if (used2 + width + spare <= budget2) {
          row2.push(index);
          used2 += width + gap;
          continue;
        }
        row = 3;
      }

      // Past both rows. A required chip still gets a place — that is the whole
      // point of having held room for it — and everything else is behind the
      // chevron.
      if (must && used2 + width <= budget2) {
        row2.push(index);
        used2 += width + gap;
        continue;
      }
      hidden.push(index);
    }

    return { row1, row2, hidden };
  };

  // Unreserved first — see the header. If everything fits without a chevron
  // there is no chevron, and that answer is final.
  const free = run(false);
  return free.hidden.length === 0 ? free : run(true);
}

export interface OneRow {
  shown: number[];
  hidden: number[];
}

/**
 * The single-row form, for the type buttons and the multi-select working set.
 *
 * Same two-pass reasoning, expressed as a lookahead: the last item never has to
 * make room for a chevron, because if it fits there is nothing left to hide.
 */
export function fillRow(
  widths: readonly number[],
  budget: number,
  options: FillOptions = {},
): OneRow {
  const gap = options.gap ?? CHIP_GAP;
  const overflowWidth = options.overflowWidth ?? OVERFLOW_WIDTH;
  const reservation = overflowWidth > 0 ? overflowWidth + gap : 0;

  const required = new Set(options.required ?? []);
  const tail = requiredTail(widths, required, gap);

  const shown: number[] = [];
  const hidden: number[] = [];
  let used = 0;
  /** Closed by the first optional chip that does not fit — see `fillRows`. */
  let closed = false;

  for (let i = 0; i < widths.length; i += 1) {
    const isLast = i === widths.length - 1;
    const limit = isLast ? budget : budget - reservation;
    const width = widths[i]!;

    // Required chips are the one thing a closed row still accepts: the
    // accounting below has been reserving their width all along.
    if (required.has(i)) {
      shown.push(i);
      used += width + gap;
      continue;
    }
    if (closed) {
      hidden.push(i);
      continue;
    }
    // An optional chip may only take room no later required chip needs.
    if (shown.length > 0 && used + width + tail[i + 1]! > limit) {
      closed = true;
      hidden.push(i);
      continue;
    }
    shown.push(i);
    used += width + gap;
  }

  return { shown, hidden };
}
